<template>
  <VVVLayoutNew>
    <template #page-title> Early Pay
    </template>
    <MDBAlert
      v-if="route.query.isImpersonation === 'true' && impersonatedUserName"
      color="info"
      static
      class="mb-4 d-flex align-items-center justify-content-between"
      style="background-color: #D7DEFF; color: var(--primary); font-weight: 600;"
    >
      <div class="d-flex align-items-center">
        <vue-feather class="mx-2" type="user" :stroke-width="2.5" />
        <span>
          Viewing as
          <b>{{ impersonatedUserName }}</b>
          [IMPERSONATING]
        </span>
      </div>

      <MDBBtn
        color="danger"
        size="sm"
        class="fw-bold"
        @click="stopImpersonation"
        style="cursor: pointer;"
      >
        Stop Impersonation
      </MDBBtn>
    </MDBAlert>
    <div class="d-flex flex-column gap-4">
      <MDBCard class="w-100 shadow-4">
        <MDBCardBody>
          <MDBCardTitle class="text-uppercase fs-4 fw-bold text-black">Status:
            <span class="primary-text">
              <template v-if="earlyPay?.status === 'Approved'">
                ACTIVATED
              </template>
              <template v-else-if="earlyPay?.status === 'Pending'">
                UNDER REVIEW
              </template>
              <template v-else> NOT ACTIVE </template>
            </span>
          </MDBCardTitle>
          <template v-if="earlyPay?.status === 'Approved'">
            <MDBCardText class="fw-bold text-success">
              Deactivate anytime to return to standard payout schedule.
            </MDBCardText>
            <MDBCardText class="mb-3">
              Based on your recent YouTube earnings and account history, you can
              now access your revenue 10 days early for a small 2.5% service
              fee.
            </MDBCardText>
            <MDBBtn class="btn btn-primary w-10 px-5 mt-1" type="button" @click="disableModal = true">
              Deactivate Plan
            </MDBBtn>
          </template>
          <template v-else-if="earlyPay?.status === 'Pending'">
            <MDBCardText class="fw-bold text-warning">
              Your Early Pay application is under review.
            </MDBCardText>
            <MDBCardText class="mb-2">
              Our team is currently evaluating your account and revenue
              activity. This usually takes 1–2 business days.
            </MDBCardText>
            <MDBCardText class="mb-3">
              You'll receive a notification once the review is complete. No
              action is needed right now.
            </MDBCardText>
          </template>
          <template v-else>
            <MDBCardText class="fw-bold text-success">
              Get your estimated YouTube earnings 10 days earlier for a
              <strong>2.5%</strong> fee.
            </MDBCardText>
            <MDBCardText class="mb-3">
              Activate Early Pay to start receiving your monthly earnings 10
              days earlier.
            </MDBCardText>
            <VVVEarlyPayModal :successToast="successToast" @trigger-success-toast="handleSuccessToast"
              @trigger-error-toast="handleErrorToast" :getSubscription="getSubscription" :isActivating="isActivating"
              :activateEarlyPay="activateEarlyPay" v-model:earlyPayoutModal="earlyPayoutModal"
              v-model:currentStep="currentStep" v-model:agreement="agreement" />
          </template>
        </MDBCardBody>
      </MDBCard>
      <div class="d-flex flex-column flex-lg-row gap-4">
        <!-- <VVVRequestWithdraw :isMobile="isMobile" @trigger-success-toast="handleSuccessToast"
                    @trigger-error-toast="handleErrorToast" /> -->
        <MDBCard v-if="earlyPay?.isEnabled && earlyPay?.status === 'Approved'" class="shadow-3"
          :class="isMobile ? 'w-100' : 'w-50 mx-auto'" text="center">
          <MDBCardHeader class="fw-bold text-black">Credit Status</MDBCardHeader>
          <MDBCardBody class="d-flex align-items-center justify-content-center">
            <MDBCardText class="fw-bold fs-4">
              <template v-if="earlyPay?.creditStatus !== 'Default'">
                <span class="text-info" v-if="earlyPay?.creditStatus === 'Pending'">
                  Processing</span>
                <span class="text-danger" v-else>Rejected</span>
              </template>
              <template v-else>
                <span class="text-success" v-if="earlyPay?.hasCreditThisMonth">
                  Credited</span>
                <span class="text-danger" v-else>Not Credited</span>
              </template>
            </MDBCardText>
          </MDBCardBody>
        </MDBCard>
        <VVVEarlyPayTransactions :isLoading="isLoading" :handlePage="handlePage" :transactionList="transactionList" />
      </div>
    </div>
    <MDBModal id="disableModal" tabindex="-1" labelledby="disableModal" v-model="disableModal">
      <MDBModalHeader class="px-4">
        <MDBModalTitle id="disableModal" class="text-black fw-bold">Are you sure?</MDBModalTitle>
      </MDBModalHeader>
      <MDBModalBody class="d-flex flex-column gap-4 p-4">
        <p>
          You're about to deactivate your <strong>Early Pay</strong> plan. This
          change will affect how you receive your earnings.
        </p>
        <div class="d-flex align-items-center gap-3 w-100">
          <MDBBtn class="fw-bold w-100 mx-auto shadow-3" type="button" @click="disableModal = false">
            Cancel
          </MDBBtn>
          <MDBBtn class="btn btn-primary w-100 mx-auto" type="button" @click="deactivateSubscription"
            :disabled="isUpdating">
            <template v-if="isUpdating"> Deactivating.. </template>
            <template v-else> Confirm </template>
          </MDBBtn>
        </div>
      </MDBModalBody>
    </MDBModal>
    <MDBToast v-model="successToast.show" id="basic-success-example" autohide position="top-right" appendToBody stacking
      width="350px" color="success" text="white" icon="fas fa-info-circle fa-lg me-2">
      <template #title>{{ successToast.title }}</template>
      {{ successToast.message }}
    </MDBToast>
    <MDBToast v-model="errorToast.show" id="basic-danger-example" autohide position="top-right" appendToBody stacking
      width="350px" color="danger" text="white" icon="fas fa-exclamation-triangle fa-lg me-2">
      <template #title>{{ errorToast.title }}</template>
      {{ errorToast.message }}
    </MDBToast>
  </VVVLayoutNew>
</template>

<script setup>
// ===== IMPORTS =====
import { useRoute } from "vue-router";
import { GetUsers } from '@/services/Users/GetUsers';
import { PostApplyEarlyPay } from "@/services/Subscriptions/PostApplyEarlyPay";
import { useUserDetailStore } from "@/store/userDetailStore";
import { useTitle } from "@vueuse/core";
import axios from "axios";
import {
  MDBAlert,
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBCardHeader,
  MDBCardText,
  MDBCardTitle,
  MDBModal,
  MDBModalBody,
  MDBModalHeader,
  MDBModalTitle,
  MDBToast,
} from "mdb-vue-ui-kit";
import VueFeather from "vue-feather";
import { useRouter } from "vue-router";
import { storeToRefs } from "pinia";
import { onBeforeUnmount, onMounted, ref, watch, watchEffect } from "vue";
import { ealryPayoutContract } from "../../../helpers/earlyPayoutContract";
import { GetCurrentSubscription } from "../../../services/Subscriptions/GetCurrentSubscription";
import { GetTransactions } from "../../../services/Subscriptions/GetTransactions";
import { PutUpdateSubscription } from "../../../services/Subscriptions/PutUpdateSubscription";
import VVVLayoutNew from "../VVVLayoutNew.vue";
import VVVEarlyPayModal from "./VVVEarlyPayModal.vue";
import VVVEarlyPayTransactions from "./VVVEarlyPayTransactions.vue";
import { useImpersonateStore } from "@/store/impersonateStore";

useTitle("Early Pay | Very Viral Videos");

// ===== STORE & ROUTE =====
const userDetailStore = useUserDetailStore();
const { userDetail } = storeToRefs(userDetailStore);
const route = useRoute(); // ✅ MOVED HERE - DECLARE BEFORE USE

// ===== IMPERSONATION STATE =====
const impersonatedUserId = ref(null); // ✅ MOVED HERE
const impersonatedUserName = ref(''); // ✅ MOVED HERE

// ===== MOBILE DETECTION =====
const isMobile = ref(window.innerWidth <= 767);
const updateResolution = () => {
  isMobile.value = window.innerWidth <= 767;
};
const router = useRouter();
const impersonateStore = useImpersonateStore();
// 🟢 Stop impersonation handler (same as in Earnings)
const stopImpersonation = () => {
  impersonateStore.isImpersonating = false;
  impersonateStore.userImpersonateDetail = {
    email: "",
    firstName: "",
    id: "",
    lastName: "",
    userTypeId: "",
    isDistributor: false,
    googleSheetLink: null,
  };
  router.push("/users");
};
onMounted(() => {
  window.addEventListener("resize", updateResolution);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateResolution);
});

// ===== TOAST STATE =====
const errorToast = ref({
  title: "Error", // ✅ FIXED: Was "Success" before
  show: false,
  message: "",
});

const successToast = ref({
  title: "Success", // ✅ FIXED: Was "Error" before
  show: false,
  message: "",
});

const handleSuccessToast = ({ show, title, message }) => {
  successToast.value.show = show;
  successToast.value.title = title;
  successToast.value.message = message;
};

const handleErrorToast = ({ show, title, message }) => {
  errorToast.value.show = show;
  errorToast.value.title = title;
  errorToast.value.message = message;
};

// ===== SUBSCRIPTION STATE =====
const earlyPay = ref({});
const isFetching = ref(false);

// ===== SUBSCRIPTION FUNCTIONS (with debugging) =====
const getSubscription = async () => {

  isFetching.value = true;
  try {
    const params = {};
    // // Check if we're impersonating
    // if (route.query.isImpersonation === 'true' && impersonatedUserId.value) {
    //   params.userId = impersonatedUserId.value;
    //   params.isImpersonation = true;
    //   console.log("✅ Using impersonation params:", params);
    // } else {
    //   console.log("ℹ️ No impersonation - using current user");
    // }
    
   
    const response = await GetCurrentSubscription(params);
    
    if (response) {
      earlyPay.value = response[0] || {};
     
    }
  } catch (error) {
    console.error("❌ getSubscription error:", error);
  } finally {
    isFetching.value = false;
    
  }
};

// ===== TRANSACTION STATE =====
const currentPageNumber = ref(null);
const transactionList = ref({
  data: [],
  pagination: {},
});
const isLoading = ref(false);

const getTransactions = async () => {
    isLoading.value = true;
    const params = {};

    if (currentPageNumber.value !== null) {
      params.pageNumber = currentPageNumber.value;
    }

    // Check if we're impersonating
    if (route.query.isImpersonation === 'true' && impersonatedUserId.value) {
      params.userId = impersonatedUserId.value;
      params.isImpersonation = true;
    }

    try {
      const response = await GetTransactions(params);
      transactionList.value.data = response.data;
      transactionList.value.pagination = response.pagination;
    } catch (error) {
      errorToast.value.show = true;
      errorToast.value.message = `Error fetching transactions: ${error}`;
    } finally {
      isLoading.value = false;
    }
};

const handlePage = (currentPage) => {
  event.preventDefault();
  currentPageNumber.value = currentPage;
  getTransactions();
};

watch(
  () => route.query.userId,
  async (userId) => {
    if (userId && route.query.isImpersonation === 'true') {
      impersonatedUserId.value = parseInt(userId);

      try {
        // Fetch user details to display their name
        const user = await GetUsers(userId);
        impersonatedUserName.value = `${user.firstName} ${user.lastName}`;

        // Fetch data AFTER we have the user ID
        await getSubscription();
        await getTransactions();
      } catch (error) {
        errorToast.value.show = true;
        errorToast.value.message = `Error fetching user: ${error}`;
      }
    } else {
      // Reset to current user
      impersonatedUserId.value = null;
      impersonatedUserName.value = '';

      // Only fetch if we have a current user
      if (userDetail.value.id) {
        await getSubscription();
        await getTransactions();
      }
    }
  },
  { immediate: true }
);

watch(
  () => userDetail.value.id,
  async (newId) => {
    // Only fetch if:
    // 1. We have a user ID
    // 2. We're NOT impersonating (impersonation is handled by userId watcher)
    if (newId && route.query.isImpersonation !== 'true') {
      await getSubscription();
      await getTransactions();
    }
  },
  { immediate: true }
);

// ===== AGREEMENT STATE =====
const agreement = ref({
  tos: false,
  signature: null,
  selfie: null,
  uploadedId: [],
  contract: null,
});

const ipAddress = ref("");
watchEffect(() => {
  const fetchIp = async () => {
    try {
      const response = await axios.get("https://api.ipify.org?format=json");
      ipAddress.value = response.data.ip;
    } catch (error) {
      console.error("Error fetching IP address:", error);
    }
  };

  fetchIp();
});

const currentStep = ref("main");
const earlyPayoutModal = ref(false);
const isActivating = ref(false);

// ===== ACTIVATE EARLY PAY =====
const activateEarlyPay = async () => {
  if (!agreement.value.signature) {
    errorToast.value.show = true;
    errorToast.value.title = "Signature Required";
    errorToast.value.message =
      "Please provide your signature to confirm the agreement";
    return;
  }

  agreement.value.contract = ealryPayoutContract(
    userDetailStore.userDetail,
    ipAddress.value,
    agreement.value.signature
  );

  isActivating.value = true;

  try {
    const formData = new FormData();
    formData.append("Status", "Pending");
    formData.append("CreditStatus", 'Default');

    if (!earlyPay?.value && !earlyPay?.value.isEnabled) {
      formData.append("IsEnabled", false);
    } else {
      formData.append("SubscriptionType", "EarlyPay");
    }
    
    // Include impersonation data if applicable
    if (route.query.isImpersonation === 'true' && impersonatedUserId.value) {
      formData.append("UserId", impersonatedUserId.value);
      formData.append("IsImpersonation", true);
    }

    // Convert selfie data URL to Blob if needed
    if (agreement.value.selfie) {
      const selfieFile = await dataUrlToFile(
        agreement.value.selfie,
        "selfie.jpg"
      );
      formData.append("KycSelfie", selfieFile);
    }

    // Handle contract file
    if (agreement.value.contract) {
      if (agreement.value.contract instanceof File) {
        formData.append("KycContract", agreement.value.contract);
      } else {
        const contractFile = await dataUrlToFile(
          agreement.value.contract,
          "contract.pdf"
        );
        formData.append("KycContract", contractFile);
      }
    }

    // Process ID files
    for (const file of agreement.value.uploadedId) {
      if (file instanceof File) {
        formData.append("KycIds", file);
      } else if (typeof file === "string" && file.startsWith("data:")) {
        const idFile = await dataUrlToFile(file, `id-${Date.now()}.jpg`);
        formData.append("KycIds", idFile);
      }
    }

    if (!earlyPay?.value && !earlyPay?.value.isEnabled) {
      await PutUpdateSubscription(earlyPay?.value.id, formData);
    } else {
      await PostApplyEarlyPay(formData);
    }

    successToast.value.show = true;
    successToast.value.title = "Success";
    successToast.value.message = "Request has been successfully sent.";
    getSubscription();
    getTransactions();
    earlyPayoutModal.value = false;
    currentStep.value = "main";
  } catch (error) {
    console.error(error);
    errorToast.value.show = true;
    errorToast.value.title = "Error";
    errorToast.value.message = `Please try again. Failed to submit request: ${error}`;
  } finally {
    isActivating.value = false;
  }
};

// Helper function to convert data URL to File object
const dataUrlToFile = (dataUrl, filename) => {
  return new Promise((resolve, reject) => {
    try {
      const arr = dataUrl.split(",");
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);

      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }

      resolve(new File([u8arr], filename, { type: mime }));
    } catch (e) {
      reject(new Error("Failed to convert data URL to file"));
    }
  });
};

watch(
  () => earlyPayoutModal.value,
  (newVal) => {
    if (!newVal) {
      agreement.value = {
        tos: false,
        signature: null,
        selfie: null,
        uploadedId: [],
        contract: null,
      };
    }
  }
);

// ===== DEACTIVATE SUBSCRIPTION =====
const disableModal = ref(false);
const isUpdating = ref(false);

const deactivateSubscription = async () => {
  isUpdating.value = true;

  try {
    const formData = new FormData();
    formData.append("IsEnabled", false);
    formData.append("Status", "Deactivated");
    formData.append("CreditStatus", 'Default');
    
    // Include impersonation data if applicable
    if (route.query.isImpersonation === 'true' && impersonatedUserId.value) {
      formData.append("UserId", impersonatedUserId.value);
      formData.append("IsImpersonation", true);
    }

    await PutUpdateSubscription(earlyPay?.value.id, formData);
    successToast.value.show = true;
    successToast.value.title = "Early Pay Subscription";
    successToast.value.message = "Plan has been deactivated successfully.";
    getSubscription();
    getTransactions();
    disableModal.value = false;
  } catch (error) {
    console.error(error);
    errorToast.value.show = true;
    errorToast.value.title = "Error";
    errorToast.value.message = `Please try again. Failed to execute request: ${error}`;
  } finally {
    isUpdating.value = false;
  }
};
</script>

<style scoped>
:root {
  --white: #ffffff;
  --primary: #223698;
  --secondary: #e2e6ef;
  --accent: #02061b;
  --blue: #1f87fc;
  --mdb-primary: var(--primary);
}

.btn-primary {
  background-color: var(--primary);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}

.btn-outline-primary {
  color: var(--primary);
  border-color: var(--primary);
}

.border-primary {
  border-color: var(--primary);
}

#table-container {
  flex: 1 1 auto;
  max-height: 35vh;
}

.image-container {
  position: relative;
  display: inline-block;
}

.image-container .loader {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  width: 45px;
  height: 45px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: BorderSpin 1s linear infinite;
}

.image-container img {
  display: block;
  width: 45px;
  height: 45px;
  border-radius: 50%;
}
</style>
